import { useState, useMemo, useCallback } from "react";
import { useTheme } from "../../../hooks/useTheme";

export interface Timeslot {
	start_time: string;
	end_time: string;
}

const BOOKING_FORM_URL = "https://readdy.ai/api/form/d70u6qj2m2156g4s73q0";
const FORMSPREE_URL = "https://formspree.io/f/mojpoooo";
const WHATSAPP_BIZ = "18495388899";

export interface CalendarTexts {
	stepDate: string;
	stepTime: string;
	stepForm: string;
	prevMonth: string;
	nextMonth: string;
	noSlots: string;
	backToDate: string;
	modifyTime: string;
	yourBooking: string;
	dateLabel: string;
	timeLabel: string;
	labelName: string;
	labelPhone: string;
	labelEmail: string;
	labelBizType: string;
	labelDemoMode: string;
	demoModeRemote: string;
	demoModeInPerson: string;
	placeholderName: string;
	placeholderPhone: string;
	placeholderEmail: string;
	placeholderBizType: string;
	bizTypeOptions: string[];
	submit: string;
	submitting: string;
	successTitle: string;
	successMsg: string;
	successEmailMsg: string;
	errorMsg: string;
	weekDays: string[];
	months: string[];
	loading: string;
	legendAvailable: string;
	legendSelected: string;
	slotDuration: string;
	sessionLimitMsg: string;
	confirmationTitle: string;
	confirmationDate: string;
	confirmationTime: string;
	confirmationName: string;
	confirmationPhone: string;
	confirmationEmail: string;
	confirmationBizType: string;
	confirmationMode: string;
	confirmationNote: string;
	confirmationNewBooking: string;
	slotsWeekFew: string;
	slotsWeekSome: string;
	slotsWeekMany: string;
	urgencyLast: string;
	urgencyFew: string;
	waNotifyBtn: string;
	labelPhoneIsWhatsapp: string;
	// Appointment type
	appointmentTypeLabel: string;
	appointmentTypeOptions: string[];
	confirmApptType: string;
	appointmentTypeOtherPlaceholder: string;
}

interface BookingCalendarProps {
	texts: CalendarTexts;
	containerStyle?: React.CSSProperties;
}

// ─── Module-level in-memory store ──────────────────────────────
const inMemoryBooked = new Set<string>();

const BOOKED_SLOTS_KEY = "innoflex_booked_slots";
const SESSION_KEY = "innoflex_session_booked";

function getBookedSlots(): string[] {
	let stored: string[] = [];
	try {
		stored = JSON.parse(localStorage.getItem(BOOKED_SLOTS_KEY) || "[]");
	} catch {
		/* ignore */
	}
	return [...new Set([...stored, ...inMemoryBooked])];
}

function markSlotBooked(startTime: string): void {
	inMemoryBooked.add(startTime);
	try {
		const booked = JSON.parse(
			localStorage.getItem(BOOKED_SLOTS_KEY) || "[]",
		) as string[];
		if (!booked.includes(startTime)) {
			localStorage.setItem(
				BOOKED_SLOTS_KEY,
				JSON.stringify([...booked, startTime]),
			);
		}
	} catch {
		/* ignore */
	}
}

function hasSessionBooked(): boolean {
	try {
		return sessionStorage.getItem(SESSION_KEY) === "1";
	} catch {
		return false;
	}
}
function setSessionBooked(): void {
	try {
		sessionStorage.setItem(SESSION_KEY, "1");
	} catch {
		/* ignore */
	}
}

function pad2(n: number): string {
	return String(n).padStart(2, "0");
}

function formatDatetimeLocal(d: Date): string {
	return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:00:00`;
}
function extractDate(dt: string): string {
	return dt.substring(0, 10);
}
function extractTime(dt: string): string {
	return dt.substring(11, 16);
}

/** Convert "HH:MM" → "H:MM AM/PM" */
function to12h(time: string): string {
	const [hStr, mStr] = time.split(":");
	const h = parseInt(hStr, 10);
	const period = h >= 12 ? "PM" : "AM";
	const h12 = h % 12 === 0 ? 12 : h % 12;
	return `${h12}:${mStr ?? "00"} ${period}`;
}

function getTodayStr(): string {
	const d = new Date();
	return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function generateLocalSlots(): Timeslot[] {
	const slots: Timeslot[] = [];
	const now = new Date();
	const limit = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
	const dayStart = new Date(now);
	dayStart.setHours(0, 0, 0, 0);
	while (dayStart <= limit) {
		const dow = dayStart.getDay();
		if (dow >= 1 && dow <= 6) {
			for (let hour = 9; hour <= 16; hour++) {
				const start = new Date(dayStart);
				start.setHours(hour, 0, 0, 0);
				if (start <= now) continue;
				const end = new Date(start);
				end.setHours(hour + 1, 0, 0, 0);
				slots.push({
					start_time: formatDatetimeLocal(start),
					end_time: formatDatetimeLocal(end),
				});
			}
		}
		dayStart.setDate(dayStart.getDate() + 1);
	}
	return slots;
}

function buildCalendarGrid(yearMonth: string): (string | null)[][] {
	const [year, month] = yearMonth.split("-").map(Number);
	const firstDay = new Date(year, month - 1, 1).getDay();
	const daysInMonth = new Date(year, month, 0).getDate();
	const cells: (string | null)[] = [];
	for (let i = 0; i < firstDay; i++) cells.push(null);
	for (let d = 1; d <= daysInMonth; d++) cells.push(`${yearMonth}-${pad2(d)}`);
	while (cells.length % 7 !== 0) cells.push(null);
	const weeks: (string | null)[][] = [];
	for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
	return weeks;
}
function prevMonthStr(ym: string): string {
	const [y, m] = ym.split("-").map(Number);
	const d = new Date(y, m - 2, 1);
	return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}
function nextMonthStr(ym: string): string {
	const [y, m] = ym.split("-").map(Number);
	const d = new Date(y, m, 1);
	return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}

// ─── WhatsApp builders ────────────────────────────────────────
function buildBizWhatsApp(booking: {
	date: string;
	start: string;
	end: string;
	name: string;
	phone: string;
	email: string;
	bizType: string;
	mode: string;
	apptType: string;
}): string {
	const lines = [
		"📋 Nueva Cita Agendada — InnoFlex",
		"",
		"📌 Tipo: " + booking.apptType,
		"",
		"👤 Cliente: " + booking.name,
		"📱 Telefono: " + booking.phone,
		"📧 Correo: " + booking.email,
		"",
		"📅 Fecha: " + booking.date,
		"⏰ Horario: " + booking.start + " — " + booking.end,
		"🎯 Modalidad: " + booking.mode,
		...(booking.bizType ? ["🏢 Negocio: " + booking.bizType] : []),
		"",
		"Responde para confirmar asistencia.",
	];
	return `https://wa.me/${WHATSAPP_BIZ}?text=${encodeURIComponent(lines.join("\n"))}`;
}

function buildClientWhatsApp(data: {
	phone: string;
	name: string;
	date: string;
	start: string;
	end: string;
	mode: string;
	apptType: string;
}): string {
	const cleanPhone = data.phone.replace(/\D/g, "");
	const lines = [
		"✅ Cita Confirmada — InnoFlex",
		"",
		"Hola " + data.name + ", tu cita esta lista:",
		"",
		"📌 Tipo: " + data.apptType,
		"📅 Fecha: " + data.date,
		"⏰ Hora: " + data.start + " — " + data.end,
		"🎯 Modalidad: " + data.mode,
		"",
		"Estaremos contigo puntualmente.",
		"Si necesitas reagendar, respondenos aqui.",
		"",
		"Equipo InnoFlex 🚀",
	];
	return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(lines.join("\n"))}`;
}

// ─── Appointment type icons map (by index) ────────────────────
const APPT_ICONS = [
	"ri-presentation-line",
	"ri-discuss-line",
	"ri-rocket-line",
	"ri-global-line",
	"ri-more-2-line",
];

// ─── Main component ───────────────────────────────────────────
export function BookingCalendar({
	texts,
	containerStyle,
}: BookingCalendarProps) {
	const { theme } = useTheme();
	const isLight = theme === "light";

	const allSlots = useMemo(() => generateLocalSlots(), []);

	const [bookedLocally, setBookedLocally] = useState<string[]>(() =>
		getBookedSlots(),
	);
	const [sessionBooked, setSessionBookedState] = useState<boolean>(() =>
		hasSessionBooked(),
	);

	const [viewMonth, setViewMonth] = useState<string>(() => {
		const now = new Date();
		return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;
	});
	const [currentStep, setCurrentStep] = useState(0);
	const [selectedDate, setSelectedDate] = useState("");
	const [selectedSlot, setSelectedSlot] = useState<Timeslot | null>(null);

	const [formName, setFormName] = useState("");
	const [formPhone, setFormPhone] = useState("");
	const [formEmail, setFormEmail] = useState("");
	const [formBizType, setFormBizType] = useState("");
	const [formDemoMode, setFormDemoMode] = useState<"remote" | "in_person">(
		"remote",
	);
	const [formPhoneIsWhatsApp, setFormPhoneIsWhatsApp] = useState(false);
	const [formApptType, setFormApptType] = useState("");
	const [formApptTypeCustom, setFormApptTypeCustom] = useState("");

	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState("");
	const [confirmedBooking, setConfirmedBooking] = useState<{
		date: string;
		start: string;
		end: string;
		name: string;
		phone: string;
		email: string;
		bizType: string;
		mode: string;
		phoneIsWhatsApp: boolean;
		apptType: string;
	} | null>(null);

	const border = isLight ? "rgba(10,22,40,0.12)" : "rgba(255,255,255,0.10)";
	const textMain = isLight ? "#0a1628" : "#ffffff";
	const textSub = isLight ? "rgba(10,22,40,0.55)" : "rgba(255,255,255,0.48)";
	const inputBg = isLight ? "#f0f6ff" : "rgba(255,255,255,0.06)";

	const availableSlots = useMemo(
		() => allSlots.filter((s) => !bookedLocally.includes(s.start_time)),
		[allSlots, bookedLocally],
	);

	const availableDates = useMemo(
		() => new Set(availableSlots.map((s) => extractDate(s.start_time))),
		[availableSlots],
	);

	const slotsForDate = useMemo(
		() =>
			availableSlots
				.filter((s) => extractDate(s.start_time) === selectedDate)
				.sort((a, b) => a.start_time.localeCompare(b.start_time)),
		[availableSlots, selectedDate],
	);

	const handleDateSelect = (date: string) => {
		setSelectedDate(date);
		setCurrentStep(1);
	};

	const handleSlotSelect = useCallback((slot: Timeslot) => {
		setSelectedSlot(slot);
		setCurrentStep(2);
	}, []);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!selectedSlot) return;
		setSubmitting(true);
		setSubmitError("");
		try {
			const modeLabel =
				formDemoMode === "remote"
					? texts.demoModeRemote
					: texts.demoModeInPerson;
			const dateStr = extractDate(selectedSlot.start_time);
			const startStr = to12h(extractTime(selectedSlot.start_time));
			const endStr = to12h(extractTime(selectedSlot.end_time));

			const isOtro =
				formApptType ===
				texts.appointmentTypeOptions[texts.appointmentTypeOptions.length - 1];
			const apptTypeValue =
				isOtro && formApptTypeCustom.trim()
					? `${formApptType}: ${formApptTypeCustom.trim()}`
					: formApptType;

			const params = new URLSearchParams();
			params.append("name", formName);
			params.append("phone", formPhone);
			params.append("email", formEmail);
			params.append("_replyto", formEmail);
			params.append("date", dateStr);
			params.append("time", `${startStr} - ${endStr}`);
			if (apptTypeValue) params.append("appointment_type", apptTypeValue);
			if (formBizType) params.append("business_type", formBizType);
			params.append("demo_mode", modeLabel);
			params.append(
				"message",
				`Nueva cita agendada por ${formName} (${formEmail}) para ${dateStr} de ${startStr} a ${endStr}.`,
			);

			const formspreeParams = new URLSearchParams(params);
			formspreeParams.append("_subject", "Nueva cita agendada - InnoFlex");

			const [bookingRes, formspreeRes] = await Promise.allSettled([
				fetch(BOOKING_FORM_URL, {
					method: "POST",
					headers: { "Content-Type": "application/x-www-form-urlencoded" },
					body: params.toString(),
				}),
				fetch(FORMSPREE_URL, {
					method: "POST",
					headers: {
						Accept: "application/json",
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body: formspreeParams.toString(),
				}),
			]);

			if (
				bookingRes.status !== "fulfilled" ||
				!bookingRes.value.ok ||
				formspreeRes.status !== "fulfilled" ||
				!formspreeRes.value.ok
			)
				throw new Error(
					`${texts.errorMsg} (ready: ${bookingRes.status === "fulfilled" ? bookingRes.value.status : "network"}, formspree: ${formspreeRes.status === "fulfilled" ? formspreeRes.value.status : "network"})`,
				);

			const bookingData = {
				date: dateStr,
				start: startStr,
				end: endStr,
				name: formName,
				phone: formPhone,
				email: formEmail,
				bizType: formBizType,
				mode: modeLabel,
				phoneIsWhatsApp: formPhoneIsWhatsApp,
				apptType: apptTypeValue,
			};

			markSlotBooked(selectedSlot.start_time);
			setBookedLocally((prev) => [...prev, selectedSlot.start_time]);

			setSessionBooked();
			setSessionBookedState(true);
			setConfirmedBooking(bookingData);

			if (formPhoneIsWhatsApp) {
				window.open(
					buildClientWhatsApp({
						phone: formPhone,
						name: formName,
						date: dateStr,
						start: startStr,
						end: endStr,
						mode: modeLabel,
						apptType: apptTypeValue,
					}),
					"_blank",
					"noopener,noreferrer",
				);
			}
		} catch (err: unknown) {
			setSubmitError(err instanceof Error ? err.message : texts.errorMsg);
		}
		setSubmitting(false);
	};

	const handleNewBooking = () => {
		setConfirmedBooking(null);
		setSessionBookedState(false);
		try {
			sessionStorage.removeItem(SESSION_KEY);
		} catch {
			/* ignore */
		}
		setSelectedDate("");
		setSelectedSlot(null);
		setFormName("");
		setFormPhone("");
		setFormEmail("");
		setFormBizType("");
		setFormDemoMode("remote");
		setFormPhoneIsWhatsApp(false);
		setFormApptType("");
		setFormApptTypeCustom("");
		setCurrentStep(0);
	};

	const [year, monthIdx] = viewMonth.split("-").map(Number);
	const monthName = texts.months[monthIdx - 1] ?? "";
	const calGrid = buildCalendarGrid(viewMonth);
	const today = getTodayStr();

	if (confirmedBooking) {
		return (
			<ConfirmationCard
				booking={confirmedBooking}
				isLight={isLight}
				border={border}
				textMain={textMain}
				textSub={textSub}
				texts={texts}
				onNewBooking={handleNewBooking}
			/>
		);
	}

	return (
		<div style={containerStyle} className="flex flex-col h-full">
			{/* Step indicator */}
			<div className="flex items-center justify-center gap-1 mb-5">
				{[texts.stepDate, texts.stepTime, texts.stepForm].map((label, i) => (
					<div key={i} className="flex items-center gap-1">
						<div className="flex items-center gap-1.5">
							<div
								className="w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold font-heading transition-all duration-300"
								style={{
									backgroundColor:
										currentStep >= i
											? "#00cfff"
											: isLight
												? "rgba(10,22,40,0.10)"
												: "rgba(255,255,255,0.10)",
									color: currentStep >= i ? "#020817" : textSub,
								}}
							>
								{currentStep > i ? (
									<i className="ri-check-line text-xs" />
								) : (
									i + 1
								)}
							</div>
							<span
								className="font-body text-xs font-semibold hidden sm:block whitespace-nowrap"
								style={{ color: currentStep === i ? "#00cfff" : textSub }}
							>
								{label}
							</span>
						</div>
						{i < 2 && (
							<div
								className="w-5 h-px mx-1"
								style={{
									backgroundColor: currentStep > i ? "#00cfff" : border,
								}}
							/>
						)}
					</div>
				))}
			</div>

			{sessionBooked && (
				<div
					className="rounded-xl p-3.5 border mb-4 flex items-start gap-3"
					style={{
						backgroundColor: "rgba(251,191,36,0.10)",
						borderColor: "rgba(251,191,36,0.35)",
					}}
				>
					<div
						className="w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0"
						style={{
							backgroundColor: "rgba(251,191,36,0.18)",
							color: "#fbbf24",
						}}
					>
						<i className="ri-information-line text-sm" />
					</div>
					<p
						className="font-body text-xs leading-relaxed"
						style={{ color: textMain }}
					>
						{texts.sessionLimitMsg}
					</p>
				</div>
			)}

			{currentStep === 0 && (
				<CalendarStep
					grid={calGrid}
					year={year}
					monthName={monthName}
					availableDates={availableDates}
					selectedDate={selectedDate}
					today={today}
					isLight={isLight}
					border={border}
					textMain={textMain}
					textSub={textSub}
					weekDays={texts.weekDays}
					prevLabel={texts.prevMonth}
					nextLabel={texts.nextMonth}
					legendAvailable={texts.legendAvailable}
					legendSelected={texts.legendSelected}
					onPrev={() => setViewMonth(prevMonthStr(viewMonth))}
					onNext={() => setViewMonth(nextMonthStr(viewMonth))}
					onSelectDate={handleDateSelect}
				/>
			)}

			{currentStep === 1 && (
				<TimeStep
					date={selectedDate}
					slots={slotsForDate}
					slotsCount={slotsForDate.length}
					noSlotsText={texts.noSlots}
					backText={texts.backToDate}
					slotDuration={texts.slotDuration}
					isLight={isLight}
					border={border}
					textMain={textMain}
					textSub={textSub}
					urgencyLast={texts.urgencyLast}
					urgencyFew={texts.urgencyFew}
					onBack={() => setCurrentStep(0)}
					onSelect={handleSlotSelect}
				/>
			)}

			{currentStep === 2 && selectedSlot && (
				<FormStep
					slot={selectedSlot}
					formName={formName}
					formPhone={formPhone}
					formEmail={formEmail}
					formBizType={formBizType}
					formDemoMode={formDemoMode}
					formPhoneIsWhatsApp={formPhoneIsWhatsApp}
					formApptType={formApptType}
					formApptTypeCustom={formApptTypeCustom}
					submitting={submitting}
					submitError={submitError}
					isLight={isLight}
					border={border}
					textMain={textMain}
					textSub={textSub}
					inputBg={inputBg}
					texts={texts}
					setFormName={setFormName}
					setFormPhone={setFormPhone}
					setFormEmail={setFormEmail}
					setFormBizType={setFormBizType}
					setFormDemoMode={setFormDemoMode}
					setFormPhoneIsWhatsApp={setFormPhoneIsWhatsApp}
					setFormApptType={setFormApptType}
					setFormApptTypeCustom={setFormApptTypeCustom}
					onModifyTime={() => setCurrentStep(0)}
					onSubmit={handleSubmit}
				/>
			)}
		</div>
	);
}

// ─── ConfirmationCard ─────────────────────────────────────────
interface ConfirmationCardProps {
	booking: {
		date: string;
		start: string;
		end: string;
		name: string;
		phone: string;
		email: string;
		bizType: string;
		mode: string;
		phoneIsWhatsApp: boolean;
		apptType: string;
	};
	isLight: boolean;
	border: string;
	textMain: string;
	textSub: string;
	texts: CalendarTexts;
	onNewBooking: () => void;
}

function ConfirmationCard({
	booking,
	isLight,
	textMain,
	textSub,
	texts,
	onNewBooking,
}: ConfirmationCardProps) {
	const modeIcon =
		booking.mode.toLowerCase().includes("remoto") ||
		booking.mode.toLowerCase().includes("remote")
			? "ri-video-line"
			: "ri-map-pin-line";
	const rows = [
		...(booking.apptType
			? [
					{
						icon: "ri-bookmark-line",
						label: texts.confirmApptType,
						value: booking.apptType,
					},
				]
			: []),
		{
			icon: "ri-calendar-check-line",
			label: texts.confirmationDate,
			value: booking.date,
		},
		{
			icon: "ri-time-line",
			label: texts.confirmationTime,
			value: `${booking.start} – ${booking.end}`,
		},
		{ icon: modeIcon, label: texts.confirmationMode, value: booking.mode },
		{
			icon: "ri-user-3-line",
			label: texts.confirmationName,
			value: booking.name,
		},
		{
			icon: "ri-phone-line",
			label: texts.confirmationPhone,
			value: booking.phone,
		},
		{
			icon: "ri-mail-line",
			label: texts.confirmationEmail,
			value: booking.email,
		},
		...(booking.bizType
			? [
					{
						icon: "ri-store-2-line",
						label: texts.confirmationBizType,
						value: booking.bizType,
					},
				]
			: []),
	];

	const handleBizWhatsApp = () => {
		window.open(buildBizWhatsApp(booking), "_blank", "noopener,noreferrer");
	};

	return (
		<div className="flex-1 flex flex-col">
			<div className="flex flex-col items-center text-center mb-5">
				<div
					className="w-14 h-14 flex items-center justify-center rounded-full mb-3"
					style={{ backgroundColor: "rgba(16,185,129,0.15)" }}
				>
					<i
						className="ri-checkbox-circle-fill text-3xl"
						style={{ color: "#10b981" }}
					/>
				</div>
				<h3
					className="font-heading text-lg font-bold mb-1"
					style={{ color: "#10b981" }}
				>
					{texts.successTitle}
				</h3>
				<p
					className="font-body text-xs leading-relaxed max-w-xs"
					style={{ color: textSub }}
				>
					{texts.successEmailMsg.replace("{email}", booking.email)}
				</p>
			</div>

			<div
				className="rounded-2xl border overflow-hidden mb-4"
				style={{
					backgroundColor: isLight ? "#f8fbff" : "rgba(255,255,255,0.03)",
					borderColor: isLight
						? "rgba(0,207,255,0.30)"
						: "rgba(0,207,255,0.20)",
				}}
			>
				<div
					className="px-4 py-3 border-b flex items-center gap-2"
					style={{
						backgroundColor: isLight
							? "rgba(0,207,255,0.08)"
							: "rgba(0,207,255,0.10)",
						borderColor: isLight
							? "rgba(0,207,255,0.20)"
							: "rgba(0,207,255,0.15)",
					}}
				>
					<i
						className="ri-calendar-schedule-line text-sm"
						style={{ color: "#00cfff" }}
					/>
					<span
						className="font-heading text-sm font-bold"
						style={{ color: "#00cfff" }}
					>
						{texts.confirmationTitle}
					</span>
				</div>
				<div
					className="divide-y"
					style={{
						borderColor: isLight
							? "rgba(10,22,40,0.06)"
							: "rgba(255,255,255,0.06)",
					}}
				>
					{rows.map((row) => (
						<div
							key={row.label}
							className="flex items-center gap-3 px-4 py-2.5"
						>
							<div
								className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0"
								style={{
									backgroundColor: "rgba(0,207,255,0.10)",
									color: "#00cfff",
								}}
							>
								<i className={`${row.icon} text-sm`} />
							</div>
							<div className="min-w-0">
								<p className="font-body text-xs" style={{ color: textSub }}>
									{row.label}
								</p>
								<p
									className="font-heading text-sm font-semibold truncate"
									style={{ color: textMain }}
								>
									{row.value}
								</p>
							</div>
						</div>
					))}
				</div>
			</div>

			<button
				type="button"
				onClick={handleBizWhatsApp}
				className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-xl font-heading font-bold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap hover:opacity-90 active:scale-[0.98] mb-3"
				style={{ backgroundColor: "#25D366", color: "#ffffff" }}
			>
				<div className="w-5 h-5 flex items-center justify-center">
					<i className="ri-whatsapp-line text-base" />
				</div>
				{texts.waNotifyBtn}
			</button>

			<div
				className="rounded-xl p-3 border mb-4 flex items-start gap-2"
				style={{
					backgroundColor: "rgba(251,191,36,0.08)",
					borderColor: "rgba(251,191,36,0.28)",
				}}
			>
				<i
					className="ri-mail-send-line text-sm flex-shrink-0 mt-0.5"
					style={{ color: "#fbbf24" }}
				/>
				<p
					className="font-body text-xs leading-relaxed"
					style={{ color: textSub }}
				>
					{texts.confirmationNote}
				</p>
			</div>

			<button
				type="button"
				onClick={onNewBooking}
				className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border font-heading font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap hover:border-[#00cfff] hover:text-[#00cfff]"
				style={{
					borderColor: isLight
						? "rgba(10,22,40,0.15)"
						: "rgba(255,255,255,0.12)",
					color: textSub,
				}}
			>
				<i className="ri-refresh-line text-sm" />
				{texts.confirmationNewBooking}
			</button>
		</div>
	);
}

// ─── CalendarStep ─────────────────────────────────────────────
interface CalendarStepProps {
	grid: (string | null)[][];
	year: number;
	monthName: string;
	availableDates: Set<string>;
	selectedDate: string;
	today: string;
	isLight: boolean;
	border: string;
	textMain: string;
	textSub: string;
	weekDays: string[];
	prevLabel: string;
	nextLabel: string;
	legendAvailable: string;
	legendSelected: string;
	onPrev: () => void;
	onNext: () => void;
	onSelectDate: (d: string) => void;
}

function CalendarStep({
	grid,
	year,
	monthName,
	availableDates,
	selectedDate,
	today,
	isLight,
	textMain,
	textSub,
	weekDays,
	prevLabel,
	nextLabel,
	legendAvailable,
	legendSelected,
	onPrev,
	onNext,
	onSelectDate,
}: CalendarStepProps) {
	const [hovered, setHovered] = useState<string | null>(null);
	return (
		<div className="flex-1 select-none">
			<div className="flex items-center justify-between mb-4">
				<button
					type="button"
					onClick={onPrev}
					title={prevLabel}
					className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors duration-150 cursor-pointer"
					style={{
						color: textSub,
						backgroundColor: isLight
							? "rgba(10,22,40,0.06)"
							: "rgba(255,255,255,0.06)",
					}}
				>
					<i className="ri-arrow-left-s-line text-lg" />
				</button>
				<span
					className="font-heading text-sm font-bold"
					style={{ color: textMain }}
				>
					{monthName} {year}
				</span>
				<button
					type="button"
					onClick={onNext}
					title={nextLabel}
					className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors duration-150 cursor-pointer"
					style={{
						color: textSub,
						backgroundColor: isLight
							? "rgba(10,22,40,0.06)"
							: "rgba(255,255,255,0.06)",
					}}
				>
					<i className="ri-arrow-right-s-line text-lg" />
				</button>
			</div>
			<div className="grid grid-cols-7 mb-1">
				{weekDays.map((wd) => (
					<div key={wd} className="text-center py-1">
						<span
							className="font-body text-xs font-semibold"
							style={{ color: textSub }}
						>
							{wd}
						</span>
					</div>
				))}
			</div>
			<div className="flex flex-col gap-1">
				{grid.map((week, wi) => (
					<div key={wi} className="grid grid-cols-7 gap-1">
						{week.map((day, di) => {
							if (!day)
								return <div key={`e${wi}${di}`} className="aspect-square" />;
							const canSelect = availableDates.has(day) && day >= today;
							const isSelected = day === selectedDate;
							const isHov = hovered === day && canSelect;
							let bg = "transparent";
							let clr = canSelect
								? textMain
								: isLight
									? "rgba(10,22,40,0.20)"
									: "rgba(255,255,255,0.15)";
							let bc = "transparent";
							if (isSelected) {
								bg = "#00cfff";
								clr = "#020817";
								bc = "#00cfff";
							} else if (isHov) {
								bg = isLight ? "rgba(0,207,255,0.18)" : "rgba(0,207,255,0.20)";
								bc = "#00cfff";
							} else if (canSelect) {
								bc = isLight ? "rgba(0,207,255,0.35)" : "rgba(0,207,255,0.28)";
							}
							return (
								<button
									key={day}
									type="button"
									disabled={!canSelect}
									onClick={() => {
										if (canSelect) onSelectDate(day);
									}}
									onMouseEnter={() => canSelect && setHovered(day)}
									onMouseLeave={() => setHovered(null)}
									className="aspect-square flex flex-col items-center justify-center rounded-lg border font-body font-semibold transition-all duration-150"
									style={{
										backgroundColor: bg,
										color: clr,
										borderColor: bc,
										cursor: canSelect ? "pointer" : "default",
									}}
								>
									<span className="text-xs leading-none">
										{day.split("-")[2]}
									</span>
									{canSelect && !isSelected && (
										<div className="w-1 h-1 rounded-full mt-0.5 bg-[#00cfff]" />
									)}
								</button>
							);
						})}
					</div>
				))}
			</div>
			<div className="flex items-center gap-4 mt-4 justify-center">
				<div className="flex items-center gap-1.5">
					<div className="w-2 h-2 rounded-full bg-[#00cfff]" />
					<span className="font-body text-xs" style={{ color: textSub }}>
						{legendAvailable}
					</span>
				</div>
				<div className="flex items-center gap-1.5">
					<div className="w-3.5 h-3.5 rounded-full bg-[#00cfff] flex items-center justify-center">
						<i className="ri-check-line text-[8px] text-[#020817]" />
					</div>
					<span className="font-body text-xs" style={{ color: textSub }}>
						{legendSelected}
					</span>
				</div>
			</div>
		</div>
	);
}

// ─── DateUrgencyBanner ────────────────────────────────────────
function DateUrgencyBanner({
	count,
	isLight,
	textMain,
	urgencyLast,
	urgencyFew,
}: {
	count: number;
	isLight: boolean;
	textMain: string;
	urgencyLast: string;
	urgencyFew: string;
}) {
	if (count === 0 || count >= 5) return null;
	const isLast = count === 1;
	const iconColor = isLast ? "#ef4444" : "#fbbf24";
	const icon = isLast ? "ri-alarm-warning-fill" : "ri-time-fill";
	const label = isLast ? urgencyLast : urgencyFew.replace("{n}", String(count));
	return (
		<div
			className="flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 border mb-4 transition-all duration-300"
			style={{
				backgroundColor: isLight
					? "rgba(239,68,68,0.08)"
					: "rgba(239,68,68,0.12)",
				borderColor: isLast ? "rgba(239,68,68,0.40)" : "rgba(251,191,36,0.40)",
			}}
		>
			<div
				className="w-6 h-6 flex items-center justify-center rounded-lg flex-shrink-0"
				style={{ backgroundColor: `${iconColor}22`, color: iconColor }}
			>
				<i className={`${icon} text-sm`} />
			</div>
			<p
				className="font-body text-xs font-semibold flex-1"
				style={{ color: textMain }}
			>
				{label}
			</p>
			<span
				className="font-heading text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
				style={{ backgroundColor: `${iconColor}20`, color: iconColor }}
			>
				{count} {count === 1 ? "slot" : "slots"}
			</span>
		</div>
	);
}

// ─── TimeStep ─────────────────────────────────────────────────
interface TimeStepProps {
	date: string;
	slots: Timeslot[];
	slotsCount: number;
	noSlotsText: string;
	backText: string;
	slotDuration: string;
	isLight: boolean;
	border: string;
	textMain: string;
	textSub: string;
	urgencyLast: string;
	urgencyFew: string;
	onBack: () => void;
	onSelect: (s: Timeslot) => void;
}

function TimeStep({
	date,
	slots,
	slotsCount,
	noSlotsText,
	backText,
	slotDuration,
	isLight,
	border,
	textMain,
	textSub,
	urgencyLast,
	urgencyFew,
	onBack,
	onSelect,
}: TimeStepProps) {
	const [hovered, setHovered] = useState<string | null>(null);
	return (
		<div className="flex-1 flex flex-col">
			<div className="flex items-center gap-3 mb-4">
				<button
					type="button"
					onClick={onBack}
					className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-colors duration-150 flex-shrink-0"
					style={{
						backgroundColor: isLight
							? "rgba(10,22,40,0.06)"
							: "rgba(255,255,255,0.06)",
						color: textSub,
					}}
				>
					<i className="ri-arrow-left-s-line text-lg" />
				</button>
				<div>
					<p className="font-body text-xs" style={{ color: textSub }}>
						{backText}
					</p>
					<p
						className="font-heading text-sm font-bold"
						style={{ color: textMain }}
					>
						{date}
					</p>
				</div>
			</div>

			<DateUrgencyBanner
				count={slotsCount}
				isLight={isLight}
				textMain={textMain}
				urgencyLast={urgencyLast}
				urgencyFew={urgencyFew}
			/>

			{slots.length === 0 ? (
				<div className="flex-1 flex flex-col items-center justify-center gap-2">
					<div
						className="w-10 h-10 flex items-center justify-center rounded-full"
						style={{
							backgroundColor: isLight
								? "rgba(10,22,40,0.06)"
								: "rgba(255,255,255,0.06)",
							color: textSub,
						}}
					>
						<i className="ri-calendar-close-line text-xl" />
					</div>
					<p
						className="font-body text-sm text-center"
						style={{ color: textSub }}
					>
						{noSlotsText}
					</p>
				</div>
			) : (
				<div className="flex flex-col gap-2 overflow-y-auto max-h-80 pr-1">
					{slots.map((slot) => {
						const key = slot.start_time;
						const isHov = hovered === key;
						return (
							<button
								key={key}
								type="button"
								onClick={() => onSelect(slot)}
								onMouseEnter={() => setHovered(key)}
								onMouseLeave={() => setHovered(null)}
								className="flex items-center justify-between px-4 py-3.5 rounded-xl border transition-all duration-150 cursor-pointer w-full text-left"
								style={{
									backgroundColor: isHov
										? isLight
											? "rgba(0,207,255,0.12)"
											: "rgba(0,207,255,0.14)"
										: isLight
											? "#f8fbff"
											: "rgba(255,255,255,0.04)",
									borderColor: isHov ? "#00cfff" : border,
								}}
							>
								<div className="flex items-center gap-3">
									<div
										className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0"
										style={{
											backgroundColor: isHov
												? "rgba(0,207,255,0.25)"
												: "rgba(0,207,255,0.12)",
											color: "#00cfff",
										}}
									>
										<i className="ri-time-line text-base" />
									</div>
									<div>
										<p
											className="font-heading text-sm font-bold"
											style={{ color: textMain }}
										>
											{to12h(extractTime(slot.start_time))} —{" "}
											{to12h(extractTime(slot.end_time))}
										</p>
										<p
											className="font-body text-xs mt-0.5"
											style={{ color: textSub }}
										>
											{slotDuration}
										</p>
									</div>
								</div>
								<i
									className="ri-arrow-right-s-line text-lg transition-transform duration-150"
									style={{
										color: isHov ? "#00cfff" : textSub,
										transform: isHov ? "translateX(2px)" : "translateX(0)",
									}}
								/>
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
}

// ─── FormStep ─────────────────────────────────────────────────
interface FormStepProps {
	slot: Timeslot;
	formName: string;
	formPhone: string;
	formEmail: string;
	formBizType: string;
	formDemoMode: "remote" | "in_person";
	formPhoneIsWhatsApp: boolean;
	formApptType: string;
	formApptTypeCustom: string;
	submitting: boolean;
	submitError: string;
	isLight: boolean;
	border: string;
	textMain: string;
	textSub: string;
	inputBg: string;
	texts: CalendarTexts;
	setFormName: (v: string) => void;
	setFormPhone: (v: string) => void;
	setFormEmail: (v: string) => void;
	setFormBizType: (v: string) => void;
	setFormDemoMode: (v: "remote" | "in_person") => void;
	setFormPhoneIsWhatsApp: (v: boolean) => void;
	setFormApptType: (v: string) => void;
	setFormApptTypeCustom: (v: string) => void;
	onModifyTime: () => void;
	onSubmit: (e: React.FormEvent) => void;
}

function FormStep({
	slot,
	formName,
	formPhone,
	formEmail,
	formBizType,
	formDemoMode,
	formPhoneIsWhatsApp,
	formApptType,
	formApptTypeCustom,
	submitting,
	submitError,
	isLight,
	border,
	textMain,
	textSub,
	inputBg,
	texts,
	setFormName,
	setFormPhone,
	setFormEmail,
	setFormBizType,
	setFormDemoMode,
	setFormPhoneIsWhatsApp,
	setFormApptType,
	setFormApptTypeCustom,
	onModifyTime,
	onSubmit,
}: FormStepProps) {
	const date = extractDate(slot.start_time);
	const start = to12h(extractTime(slot.start_time));
	const end = to12h(extractTime(slot.end_time));
	const [focusedField, setFocusedField] = useState<string | null>(null);
	const fieldBorder = (id: string) =>
		focusedField === id ? "#00cfff" : border;
	const inputClass =
		"w-full rounded-lg border px-3 py-2.5 font-body text-sm outline-none transition-colors duration-150";

	const otroOption =
		texts.appointmentTypeOptions[texts.appointmentTypeOptions.length - 1];
	const isOtroSelected = formApptType === otroOption;

	const isSubmitDisabled =
		submitting ||
		!formApptType ||
		(isOtroSelected && !formApptTypeCustom.trim());

	const modeOptions: {
		value: "remote" | "in_person";
		icon: string;
		label: string;
	}[] = [
		{ value: "remote", icon: "ri-video-line", label: texts.demoModeRemote },
		{
			value: "in_person",
			icon: "ri-map-pin-line",
			label: texts.demoModeInPerson,
		},
	];

	return (
		<div className="flex-1 flex flex-col gap-3">
			{/* Booking summary */}
			<div
				className="rounded-xl p-3.5 border"
				style={{
					backgroundColor: isLight
						? "rgba(0,207,255,0.06)"
						: "rgba(0,207,255,0.08)",
					borderColor: isLight
						? "rgba(0,207,255,0.28)"
						: "rgba(0,207,255,0.22)",
				}}
			>
				<div className="flex items-center justify-between mb-2">
					<p
						className="font-heading text-xs font-bold"
						style={{ color: "#00cfff" }}
					>
						{texts.yourBooking}
					</p>
					<button
						type="button"
						onClick={onModifyTime}
						className="font-body text-xs font-semibold cursor-pointer hover:opacity-70 transition-opacity whitespace-nowrap"
						style={{ color: "#00cfff" }}
					>
						<i className="ri-edit-line mr-1" />
						{texts.modifyTime}
					</button>
				</div>
				<div className="flex items-center gap-4">
					<div className="flex items-center gap-1.5">
						<i
							className="ri-calendar-line text-xs"
							style={{ color: textSub }}
						/>
						<span
							className="font-heading text-xs font-semibold"
							style={{ color: textMain }}
						>
							{date}
						</span>
					</div>
					<div className="flex items-center gap-1.5">
						<i className="ri-time-line text-xs" style={{ color: textSub }} />
						<span
							className="font-heading text-xs font-semibold"
							style={{ color: textMain }}
						>
							{start} — {end}
						</span>
					</div>
				</div>
			</div>

			<form
				onSubmit={onSubmit}
				id="booking-cita-innoflex"
				data-readdy-form
				className="flex flex-col gap-2.5"
			>
				{/* Appointment type — card grid */}
				<div>
					<label
						className="font-body text-xs font-semibold mb-1.5 block"
						style={{ color: textSub }}
					>
						{texts.appointmentTypeLabel}{" "}
						<span style={{ color: "#00cfff" }}>*</span>
					</label>
					<div className="grid grid-cols-2 gap-2">
						{texts.appointmentTypeOptions.map((opt, idx) => {
							const isActive = formApptType === opt;
							const icon = APPT_ICONS[idx] ?? "ri-calendar-line";
							return (
								<button
									key={opt}
									type="button"
									onClick={() => {
										setFormApptType(opt);
										if (opt !== otroOption) setFormApptTypeCustom("");
									}}
									className="flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-all duration-150 cursor-pointer text-left"
									style={{
										backgroundColor: isActive
											? isLight
												? "rgba(0,207,255,0.10)"
												: "rgba(0,207,255,0.14)"
											: inputBg,
										borderColor: isActive ? "#00cfff" : border,
									}}
								>
									<div
										className="w-7 h-7 flex items-center justify-center rounded-md flex-shrink-0"
										style={{
											backgroundColor: isActive
												? "rgba(0,207,255,0.20)"
												: "transparent",
											color: isActive ? "#00cfff" : textSub,
										}}
									>
										<i className={`${icon} text-sm`} />
									</div>
									<span
										className="font-body text-xs font-semibold leading-tight"
										style={{ color: isActive ? "#00cfff" : textSub }}
									>
										{opt}
									</span>
									{isActive && (
										<div
											className="ml-auto w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0"
											style={{ backgroundColor: "#00cfff" }}
										>
											<i className="ri-check-line text-[9px] text-[#020817]" />
										</div>
									)}
								</button>
							);
						})}
					</div>

					{/* "Otro" custom description field */}
					{isOtroSelected && (
						<div className="mt-2">
							<textarea
								name="appointment_type_custom"
								rows={3}
								required
								value={formApptTypeCustom}
								onChange={(e) => {
									if (e.target.value.length <= 500)
										setFormApptTypeCustom(e.target.value);
								}}
								placeholder={texts.appointmentTypeOtherPlaceholder}
								onFocus={() => setFocusedField("apptCustom")}
								onBlur={() => setFocusedField(null)}
								className="w-full rounded-lg border px-3 py-2.5 font-body text-sm outline-none transition-colors duration-150 resize-none"
								style={{
									backgroundColor: inputBg,
									borderColor: fieldBorder("apptCustom"),
									color: textMain,
								}}
							/>
							<p
								className="font-body text-xs mt-1 text-right"
								style={{ color: textSub }}
							>
								{formApptTypeCustom.length}/500
							</p>
						</div>
					)}
				</div>

				{/* Demo mode */}
				<div>
					<label
						className="font-body text-xs font-semibold mb-1.5 block"
						style={{ color: textSub }}
					>
						{texts.labelDemoMode} <span style={{ color: "#00cfff" }}>*</span>
					</label>
					<div className="grid grid-cols-2 gap-2">
						{modeOptions.map((opt) => {
							const isActive = formDemoMode === opt.value;
							return (
								<button
									key={opt.value}
									type="button"
									onClick={() => setFormDemoMode(opt.value)}
									className="flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-all duration-150 cursor-pointer"
									style={{
										backgroundColor: isActive
											? isLight
												? "rgba(0,207,255,0.10)"
												: "rgba(0,207,255,0.14)"
											: inputBg,
										borderColor: isActive ? "#00cfff" : border,
									}}
								>
									<div
										className="w-7 h-7 flex items-center justify-center rounded-md flex-shrink-0"
										style={{
											backgroundColor: isActive
												? "rgba(0,207,255,0.20)"
												: "transparent",
											color: isActive ? "#00cfff" : textSub,
										}}
									>
										<i className={`${opt.icon} text-sm`} />
									</div>
									<span
										className="font-body text-xs font-semibold leading-tight text-left"
										style={{ color: isActive ? "#00cfff" : textSub }}
									>
										{opt.label}
									</span>
									{isActive && (
										<div
											className="ml-auto w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0"
											style={{ backgroundColor: "#00cfff" }}
										>
											<i className="ri-check-line text-[9px] text-[#020817]" />
										</div>
									)}
								</button>
							);
						})}
					</div>
				</div>

				{/* Name */}
				<div>
					<label
						className="font-body text-xs font-semibold mb-1 block"
						style={{ color: textSub }}
					>
						{texts.labelName} <span style={{ color: "#00cfff" }}>*</span>
					</label>
					<input
						type="text"
						name="name"
						required
						value={formName}
						onChange={(e) => setFormName(e.target.value)}
						placeholder={texts.placeholderName}
						onFocus={() => setFocusedField("name")}
						onBlur={() => setFocusedField(null)}
						className={inputClass}
						style={{
							backgroundColor: inputBg,
							borderColor: fieldBorder("name"),
							color: textMain,
						}}
					/>
				</div>

				{/* Phone + WhatsApp checkbox */}
				<div>
					<label
						className="font-body text-xs font-semibold mb-1 block"
						style={{ color: textSub }}
					>
						{texts.labelPhone} <span style={{ color: "#00cfff" }}>*</span>
					</label>
					<input
						type="tel"
						name="phone"
						required
						value={formPhone}
						onChange={(e) => setFormPhone(e.target.value)}
						placeholder={texts.placeholderPhone}
						onFocus={() => setFocusedField("phone")}
						onBlur={() => setFocusedField(null)}
						className={inputClass}
						style={{
							backgroundColor: inputBg,
							borderColor: fieldBorder("phone"),
							color: textMain,
						}}
					/>
					<button
						type="button"
						onClick={() => setFormPhoneIsWhatsApp(!formPhoneIsWhatsApp)}
						className="flex items-center gap-2 mt-2 cursor-pointer"
					>
						<div
							className="w-4 h-4 flex items-center justify-center rounded flex-shrink-0 border transition-all duration-150"
							style={{
								backgroundColor: formPhoneIsWhatsApp
									? "#25D366"
									: "transparent",
								borderColor: formPhoneIsWhatsApp
									? "#25D366"
									: isLight
										? "rgba(10,22,40,0.25)"
										: "rgba(255,255,255,0.25)",
							}}
						>
							{formPhoneIsWhatsApp && (
								<i className="ri-check-line text-[9px] text-white" />
							)}
						</div>
						<div className="flex items-center gap-1.5">
							<div className="w-4 h-4 flex items-center justify-center">
								<i
									className="ri-whatsapp-line text-sm"
									style={{ color: formPhoneIsWhatsApp ? "#25D366" : textSub }}
								/>
							</div>
							<span
								className="font-body text-xs transition-colors duration-150"
								style={{ color: formPhoneIsWhatsApp ? "#25D366" : textSub }}
							>
								{texts.labelPhoneIsWhatsapp}
							</span>
						</div>
					</button>
				</div>

				{/* Email */}
				<div>
					<label
						className="font-body text-xs font-semibold mb-1 block"
						style={{ color: textSub }}
					>
						{texts.labelEmail} <span style={{ color: "#00cfff" }}>*</span>
					</label>
					<input
						type="email"
						name="email"
						required
						value={formEmail}
						onChange={(e) => setFormEmail(e.target.value)}
						placeholder={texts.placeholderEmail}
						onFocus={() => setFocusedField("email")}
						onBlur={() => setFocusedField(null)}
						className={inputClass}
						style={{
							backgroundColor: inputBg,
							borderColor: fieldBorder("email"),
							color: textMain,
						}}
					/>
				</div>

				{/* Business type */}
				<div>
					<label
						className="font-body text-xs font-semibold mb-1 block"
						style={{ color: textSub }}
					>
						{texts.labelBizType}
					</label>
					<div className="relative">
						<select
							value={formBizType}
							onChange={(e) => setFormBizType(e.target.value)}
							onFocus={() => setFocusedField("biz")}
							onBlur={() => setFocusedField(null)}
							className={`${inputClass} pr-8 appearance-none cursor-pointer`}
							style={{
								backgroundColor: inputBg,
								borderColor: fieldBorder("biz"),
								color: formBizType ? textMain : textSub,
							}}
						>
							<option
								value=""
								style={{ backgroundColor: isLight ? "#fff" : "#0c1630" }}
							>
								{texts.placeholderBizType}
							</option>
							{texts.bizTypeOptions.map((opt) => (
								<option
									key={opt}
									value={opt}
									style={{
										backgroundColor: isLight ? "#fff" : "#0c1630",
										color: textMain,
									}}
								>
									{opt}
								</option>
							))}
						</select>
						<i
							className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-sm"
							style={{ color: textSub }}
						/>
					</div>
				</div>

				{submitError && (
					<div
						className="rounded-lg px-3 py-2.5 border"
						style={{
							backgroundColor: "rgba(248,113,113,0.08)",
							borderColor: "rgba(248,113,113,0.30)",
						}}
					>
						<p
							className="font-body text-xs leading-relaxed break-words"
							style={{ color: "#f87171" }}
						>
							{submitError}
						</p>
					</div>
				)}

				<button
					type="submit"
					disabled={isSubmitDisabled}
					className="w-full flex items-center justify-center gap-2 bg-electric text-[#020817] font-heading font-bold text-sm py-3.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap hover:bg-electric/90 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed mt-1"
				>
					{submitting ? (
						<>
							<div className="w-4 h-4 border-2 border-[#020817] border-t-transparent rounded-full animate-spin" />
							{texts.submitting}
						</>
					) : (
						<>
							<i className="ri-calendar-check-line text-base" />
							{texts.submit}
						</>
					)}
				</button>
			</form>
		</div>
	);
}
